import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useScopedPath } from '../../components/ScopedLink';
import { Link } from 'react-router-dom';
import DailyChecklistPage from './DailyChecklistPage';
import ChecklistSignOffQueue from './ChecklistSignOffQueue';
import ChecklistTemplatePage from './ChecklistTemplatePage';
import ChecklistHistoryPage from './ChecklistHistoryPage';

/**
 * One "Checklists" destination, three faces of the same feature.
 *
 * Previously these were three separate nav entries and three pages — "Daily
 * Checklists" (do them), "Checklist Sign-off" (approve completed runs), and
 * "Checklist Items" (edit the templates) — even though sign-off and items are
 * just the admin steps of the same workflow. Now they are tabs on one page.
 *
 * STAFF see Today and History; the sign-off and items surfaces are admin-only
 * and each page already guards itself, so a direct URL hit by a staff member
 * still lands on that page's own access-denied state.
 *
 * History exists because a completed checklist previously went nowhere a
 * person could see: after sign-off it was stored and no screen listed it.
 *
 * The routes are stable (/checklists, /checklists/sign-off,
 * /checklists/history, /checklists/template) so deep links — including the
 * sign-off notification target — still resolve; the active tab is derived
 * from the path.
 */

type Tab = 'today' | 'sign-off' | 'history' | 'items';

const TABS: { key: Tab; label: string; suffix: string; adminOnly?: boolean }[] = [
  { key: 'today', label: 'Today', suffix: '/checklists' },
  { key: 'sign-off', label: 'Sign-off', suffix: '/checklists/sign-off', adminOnly: true },
  { key: 'history', label: 'History', suffix: '/checklists/history' },
  { key: 'items', label: 'Manage items', suffix: '/checklists/template', adminOnly: true },
];

const ChecklistsPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const scopedPath = useScopedPath();
  const { pathname } = useLocation();

  const active: Tab = pathname.endsWith('/checklists/sign-off')
    ? 'sign-off'
    : pathname.endsWith('/checklists/template')
    ? 'items'
    : pathname.endsWith('/checklists/history')
    ? 'history'
    : 'today';

  const panel =
    active === 'sign-off' ? (
      <ChecklistSignOffQueue />
    ) : active === 'items' ? (
      <ChecklistTemplatePage />
    ) : active === 'history' ? (
      <ChecklistHistoryPage />
    ) : (
      // embedded: the tab bar above already names this section.
      <DailyChecklistPage embedded />
    );

  const visibleTabs = TABS.filter((t) => isAdmin || !t.adminOnly);

  return (
    <div data-testid="checklists-page" className="max-w-3xl mx-auto px-4 pt-8">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Checklists">
          {visibleTabs.map((t) => {
            const isActive = t.key === active;
            return (
              <Link
                key={t.key}
                to={scopedPath(t.suffix)}
                data-testid={`checklists-tab-${t.key}`}
                aria-current={isActive ? 'page' : undefined}
                className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
                  isActive
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {t.label}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Each panel is a full page with its own max-width wrapper; render it
          directly under the shared tab bar. */}
      <div>{panel}</div>
    </div>
  );
};

export default ChecklistsPage;
